import type { PolicyStatePremiumViewModel } from '@/api/types/PolicyStatePremiumViewModel';
import type { UpdatePolicyRequest } from 'src/api/services/policy.service';

import { formatDollarAmount } from 'src/sections/employer-data/shared/utils/policyUtils';

import type { PolicyDetailsEditHandle } from '../policy-details-edit.types';

export type PolicyPeriodEditDraft = {
  policyPeriodId: number;
  effectiveDate: string;
  experienceRatingCode: string;
  estimatedStandardPremium: string;
  statePremiums: PolicyPeriodStatePremiumEditDraft[];
};

export type PolicyPeriodStatePremiumEditDraft = {
  statePremiumId: number;
  policyChangeEffectiveDate: string;
  rating: string;
  premium: string;
  expirationDate: string;
};

// Changed-values-only representation of a period's draft, for cross-tab-switch persistence without duplicating unedited read data.
export type PolicyPeriodDraftDelta = Record<
  number,
  Partial<Omit<PolicyPeriodEditDraft, 'policyPeriodId' | 'statePremiums'>> & {
    statePremiums?: Record<
      number,
      Partial<Omit<PolicyPeriodStatePremiumEditDraft, 'statePremiumId'>>
    >;
  }
>;

export function getPolicyPeriodsDraftDelta(
  snapshots: Record<number, PolicyPeriodEditDraft>,
  drafts: Record<number, PolicyPeriodEditDraft>
): PolicyPeriodDraftDelta {
  const delta: PolicyPeriodDraftDelta = {};

  Object.values(drafts).forEach((draft) => {
    const snapshot = snapshots[draft.policyPeriodId];
    if (!snapshot) {
      return;
    }

    const periodDelta: PolicyPeriodDraftDelta[number] = {};
    if (draft.effectiveDate !== snapshot.effectiveDate)
      periodDelta.effectiveDate = draft.effectiveDate;
    if (draft.experienceRatingCode !== snapshot.experienceRatingCode)
      periodDelta.experienceRatingCode = draft.experienceRatingCode;
    if (draft.estimatedStandardPremium !== snapshot.estimatedStandardPremium)
      periodDelta.estimatedStandardPremium = draft.estimatedStandardPremium;

    const statePremiumDelta: NonNullable<PolicyPeriodDraftDelta[number]['statePremiums']> = {};
    draft.statePremiums.forEach((row) => {
      const previousRow = snapshot.statePremiums.find(
        (item) => item.statePremiumId === row.statePremiumId
      );
      if (!previousRow) {
        return;
      }

      const patch: Partial<Omit<PolicyPeriodStatePremiumEditDraft, 'statePremiumId'>> = {};
      if (row.policyChangeEffectiveDate !== previousRow.policyChangeEffectiveDate)
        patch.policyChangeEffectiveDate = row.policyChangeEffectiveDate;
      if (row.rating !== previousRow.rating) patch.rating = row.rating;
      if (row.premium !== previousRow.premium) patch.premium = row.premium;
      if (row.expirationDate !== previousRow.expirationDate)
        patch.expirationDate = row.expirationDate;
      if (Object.keys(patch).length > 0) statePremiumDelta[row.statePremiumId] = patch;
    });
    if (Object.keys(statePremiumDelta).length > 0) periodDelta.statePremiums = statePremiumDelta;

    if (Object.keys(periodDelta).length > 0) delta[draft.policyPeriodId] = periodDelta;
  });

  return delta;
}

export function applyPolicyPeriodsDraftDelta(
  snapshots: Record<number, PolicyPeriodEditDraft>,
  delta: PolicyPeriodDraftDelta | null | undefined
): Record<number, PolicyPeriodEditDraft> {
  if (!delta) {
    return snapshots;
  }

  const result: Record<number, PolicyPeriodEditDraft> = {};
  Object.entries(snapshots).forEach(([key, snapshot]) => {
    const policyPeriodId = Number(key);
    const periodDelta = delta[policyPeriodId];
    if (!periodDelta) {
      result[policyPeriodId] = snapshot;
      return;
    }

    const { statePremiums: statePremiumDelta, ...scalarDelta } = periodDelta;
    const merged: PolicyPeriodEditDraft = { ...snapshot, ...scalarDelta };
    if (statePremiumDelta) {
      merged.statePremiums = snapshot.statePremiums.map((row) =>
        statePremiumDelta[row.statePremiumId]
          ? { ...row, ...statePremiumDelta[row.statePremiumId] }
          : row
      );
    }
    result[policyPeriodId] = merged;
  });

  return result;
}

export type PolicyPeriodFieldErrors = {
  effectiveDate?: string;
  experienceRatingCode?: string;
  estimatedStandardPremium?: string;
  statePremiums?: Array<{
    policyChangeEffectiveDate?: string;
    rating?: string;
    premium?: string;
  }>;
};

export type PolicyPeriodsEditProps = {
  draft: PolicyPeriodEditDraft;
  setDraft: React.Dispatch<React.SetStateAction<PolicyPeriodEditDraft>>;
  experienceRatingLookups: Array<{
    experienceRatingCode: string;
    experienceRatingDescription: string;
    experienceRatingEnum: string;
  }>;
  fieldErrors: PolicyPeriodFieldErrors;
};

export type PolicyPeriodsHandle = PolicyDetailsEditHandle;

type ExperienceRatingLookup = PolicyPeriodsEditProps['experienceRatingLookups'][number];

export function normalizeRatingInput(value: string): string {
  const numericValue = value.replace(/[^\d.]/g, '');
  const [wholePart = '', ...fractionParts] = numericValue.split('.');
  const fractionPart = fractionParts.join('').slice(0, 2);
  const fraction = fractionParts.length > 0 ? `.${fractionPart}` : '';

  return `${wholePart.slice(0, 1)}${fraction}`;
}

export function normalizeRatingForSave(value: string): number {
  return Number(Number(value).toFixed(2));
}

export function isValidRating(value: string): boolean {
  return /^\d\.\d{1,2}$/.test(value) && Number(value) <= 9.99;
}

export function validatePolicyPeriodDraft(draft: PolicyPeriodEditDraft): PolicyPeriodFieldErrors {
  const errors: PolicyPeriodFieldErrors = {};
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(draft.effectiveDate)) {
    errors.effectiveDate = 'Period effective date must use MM/DD/YYYY format.';
  }
  if (!draft.experienceRatingCode) {
    errors.experienceRatingCode = 'Experience rating is required.';
  }
  if (!/^\d{1,10}$/.test(draft.estimatedStandardPremium.trim())) {
    errors.estimatedStandardPremium = 'Must be a whole number with a maximum of 10 characters.';
  }

  const statePremiumErrors = draft.statePremiums.map((row) => ({
    ...(!/^\d{2}\/\d{2}\/\d{4}$/.test(row.policyChangeEffectiveDate)
      ? { policyChangeEffectiveDate: 'Rating period must use MM/DD/YYYY format.' }
      : {}),
    ...(!isValidRating(row.rating)
      ? { rating: 'Rating must use the format N.NN, with one or two decimal places.' }
      : {}),
    ...(!/^\d{1,10}$/.test(row.premium)
      ? { premium: 'Must be a whole number with a maximum of 10 characters.' }
      : {}),
  }));
  if (statePremiumErrors.some((error) => Object.keys(error).length > 0)) {
    errors.statePremiums = statePremiumErrors;
  }

  return errors;
}

export function hasPolicyPeriodDraftChanges(
  snapshot: PolicyPeriodEditDraft,
  draft: PolicyPeriodEditDraft
): boolean {
  return JSON.stringify(snapshot) !== JSON.stringify(draft);
}

function buildPolicyPeriodStatePremiumsPayload(
  snapshot: PolicyPeriodEditDraft,
  draft: PolicyPeriodEditDraft
): NonNullable<UpdatePolicyRequest['policyPeriods']>[number]['statePremiums'] {
  return draft.statePremiums
    .filter((row) => {
      const previousRow = snapshot.statePremiums.find(
        (candidate) => candidate.statePremiumId === row.statePremiumId
      );
      return (
        previousRow &&
        (previousRow.policyChangeEffectiveDate !== row.policyChangeEffectiveDate ||
          previousRow.rating !== row.rating ||
          previousRow.premium !== row.premium)
      );
    })
    .map((row) => ({
      statePremiumId: row.statePremiumId,
      policyChangeEffectiveDate: row.policyChangeEffectiveDate || undefined,
      expModFactor: normalizeRatingForSave(row.rating),
      estimatedStateStandardPremiumTotal: Number(row.premium),
    }));
}

export function buildPolicyPeriodPayload(
  snapshot: PolicyPeriodEditDraft,
  draft: PolicyPeriodEditDraft
): NonNullable<UpdatePolicyRequest['policyPeriods']>[number] | null {
  if (!hasPolicyPeriodDraftChanges(snapshot, draft)) {
    return null;
  }

  return {
    policyPeriodId: draft.policyPeriodId,
    policyPeriodEffectiveDate: draft.effectiveDate,
    experienceRatingCode: draft.experienceRatingCode,
    estimatedStandardPremium: Number(draft.estimatedStandardPremium),
    statePremiums: buildPolicyPeriodStatePremiumsPayload(snapshot, draft),
  };
}

function addSummary(
  summaries: string[],
  label: string,
  previousValue: string | number,
  newValue: string | number
): void {
  const previous = String(previousValue ?? '').trim();
  const next = String(newValue ?? '').trim();

  if (previous === next) {
    return;
  }

  summaries.push(`${label} changed from ${previous || '[blank]'} to ${next || '[blank]'}`);
}

function addCurrencySummary(
  summaries: string[],
  label: string,
  previousValue: string,
  newValue: string
): void {
  if (previousValue.trim() === newValue.trim()) {
    return;
  }

  summaries.push(
    `${label} changed from ${formatDollarAmount(Number(previousValue)) || '[blank]'} to ${formatDollarAmount(Number(newValue)) || '[blank]'}`
  );
}

function getExperienceRatingDescription(code: string, lookups: ExperienceRatingLookup[]): string {
  return (
    lookups.find((lookup) => lookup.experienceRatingCode === code)?.experienceRatingDescription ??
    code
  );
}

export function buildPolicyPeriodChangedFieldSummaries(
  snapshot: PolicyPeriodEditDraft,
  draft: PolicyPeriodEditDraft,
  experienceRatingLookups: ExperienceRatingLookup[]
): string[] {
  const summaries: string[] = [];

  addSummary(summaries, 'Period effective date', snapshot.effectiveDate, draft.effectiveDate);
  addSummary(
    summaries,
    'Experience rating',
    getExperienceRatingDescription(snapshot.experienceRatingCode, experienceRatingLookups),
    getExperienceRatingDescription(draft.experienceRatingCode, experienceRatingLookups)
  );
  addCurrencySummary(
    summaries,
    'Estimated standard premium total',
    snapshot.estimatedStandardPremium,
    draft.estimatedStandardPremium
  );

  draft.statePremiums.forEach((row) => {
    const previousRow = snapshot.statePremiums.find(
      (candidate) => candidate.statePremiumId === row.statePremiumId
    );

    if (!previousRow) {
      return;
    }

    addSummary(
      summaries,
      'Rating period',
      previousRow.policyChangeEffectiveDate,
      row.policyChangeEffectiveDate
    );
    addSummary(summaries, 'Rating', previousRow.rating, row.rating);
    addCurrencySummary(summaries, 'Premium', previousRow.premium, row.premium);
  });

  return summaries;
}

export function initializePolicyPeriodDraft(period: {
  policyPeriodId?: number;
  effectiveDate?: string;
  experienceRatingCode?: number | null;
  estimatedStandardPremiumTotal?: number;
  policyStatePremiums?: Array<PolicyStatePremiumViewModel> | null;
}): PolicyPeriodEditDraft {
  return {
    policyPeriodId: period.policyPeriodId ?? 0,
    effectiveDate: period.effectiveDate ? formatDateValue(period.effectiveDate) : '',
    experienceRatingCode: String(period.experienceRatingCode ?? ''),
    estimatedStandardPremium: String(Math.trunc(period.estimatedStandardPremiumTotal ?? 0)),
    statePremiums: (period.policyStatePremiums ?? []).map((statePremium) => ({
      statePremiumId: statePremium.statePremiumId ?? 0,
      policyChangeEffectiveDate: statePremium.policyChangeEffectiveDate
        ? formatDateValue(statePremium.policyChangeEffectiveDate)
        : '',
      rating: statePremium.expModFactor?.toFixed(2) ?? '',
      premium: String(Math.trunc(statePremium.estimatedStateStandardPremiumTotal ?? 0)),
      expirationDate: statePremium.policyChangeExpirationDate
        ? formatDateValue(statePremium.policyChangeExpirationDate)
        : '',
    })),
  };
}

function formatDateValue(value: string): string {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
}
